/**
 * Generate strap mockup + leather hide overview images.
 *
 * @param {object} params
 * @param {object} params.config           - { width, buckleWidth, shortLength, longLength } in mm
 * @param {number} params.hideWidthMm      - leather width in mm
 * @param {number} params.hideHeightMm     - leather height in mm
 * @param {string} params.imageUrl         - leather image URL
 * @param {object} params.shortPos         - { x, y } in trueWidth-space px
 * @param {object} params.longPos          - { x, y } in trueWidth-space px
 * @param {number} params.scale            - px/mm screen scale
 * @param {number} params.strapRotation    - rotation degrees
 * @param {string} params.cutOption        - "freemove" | "preselected"
 * @param {object} params.preSelectedSize  - ratio coords for preselected cut zones
 * @param {number} params.trueWidth        - leather display width in px
 * @param {number} params.trueHeight       - leather display height in px
 * @param {number} params.imgDisplayHeight - displayed image height in px
 * @param {number} params.yScale           - capDisplayHeight / trueHeight
 * @param {number} params.vWidth           - strap width in trueWidth-space px
 * @param {number} params.vBuckleWidth     - buckle width in trueWidth-space px
 * @param {number} params.vShort           - short strap length in trueWidth-space px
 * @param {number} params.vLong            - long strap length in trueWidth-space px
 * @param {number} params.tipH             - tip height in trueWidth-space px
 *
 * @returns {Promise<{base64Image: string, hideOverview: string}>}
 */
export async function generateStrapImages(params) {
    const {
        config, hideWidthMm, imageUrl,
        shortPos, longPos, scale, strapRotation,
        cutOption, preSelectedSize,
        trueWidth, trueHeight, imgDisplayHeight, yScale,
        vWidth, vBuckleWidth, vShort, vLong, tipH,
    } = params;

    const SCALE = 2;
    const PAD   = 24;

    // Fixed 800px reference for leather width → strap/leather ratio always correct
    const ms = 800 / hideWidthMm;

    const cvW      = config.width * ms;
    const cvBW     = (config.buckleWidth || config.width) * ms;
    const cvS      = config.shortLength * ms;
    const cvL      = config.longLength * ms;
    const cvFullW  = Math.max(cvW, cvBW);
    const cvLugOff = (cvFullW - cvW) / 2;
    const cvBucOff = (cvFullW - cvBW) / 2;
    const cvTip    = 26 * ms;
    const cvSW     = (0.75 * ms) / 2;

    const cvFG      = 18 * ms;
    const cvMaxS    = 110 * ms;
    const cvMaxL    = 160 * ms;
    const cvPX      = window.innerWidth < 768 ? 60 : 80;
    const cvPY      = 80;
    const cvBoxW    = cvFullW + cvPX;
    const cvBoxH    = cvMaxS + cvFG + cvMaxL + cvPY;
    const cvAnchorY = cvMaxS + (cvPY / 2) + (cvFG / 2);
    const cvShortL  = cvPX / 2;
    const cvShortT  = cvAnchorY - (cvFG / 2) - cvS;
    const cvLongL   = cvPX / 2;
    const cvLongT   = cvAnchorY + (cvFG / 2);

    const canvas  = document.createElement('canvas');
    canvas.width  = (cvBoxW + PAD * 2) * SCALE;
    canvas.height = (cvBoxH + PAD * 2) * SCALE;
    const ctx = canvas.getContext('2d');
    ctx.scale(SCALE, SCALE);

    // Load leather texture
    let img = null;
    if (imageUrl) {
        try {
            img = await new Promise((res) => {
                const i = new Image();
                i.crossOrigin = 'anonymous';
                i.onload  = () => res(i);
                i.onerror = () => {
                    const i2 = new Image();
                    i2.onload  = () => res(i2);
                    i2.onerror = () => res(null);
                    i2.src = imageUrl;
                };
                i.src = imageUrl;
            });
        } catch (_) { img = null; }
    }

    const OX = PAD, OY = PAD;

    const cvTrueW = hideWidthMm * ms;
    const cvTrueH = params.hideHeightMm * ms;
    let cvCapH      = cvTrueH;
    let cvCapYScale = 1;
    if (img && img.naturalWidth > 0) {
        cvCapH      = cvTrueW * (img.naturalHeight / img.naturalWidth);
        cvCapYScale = cvTrueH > 0 ? cvCapH / cvTrueH : 1;
    }

    const cvSPX = (shortPos.x / scale) * ms;
    const cvSPY = (shortPos.y / scale) * ms;
    const cvLPX = (longPos.x / scale) * ms;
    const cvLPY = (longPos.y / scale) * ms;

    let capDisplayHeight = imgDisplayHeight;
    let capYScale = yScale;
    if (img && img.naturalWidth > 0) {
        capDisplayHeight = trueWidth * (img.naturalHeight / img.naturalWidth);
        capYScale = trueHeight > 0 ? capDisplayHeight / trueHeight : 1;
    }

    const screenRatio = scale > 0 ? ms / scale : ms / 4;

    // ── Short strap (buckle at top, lug at bottom) ───────────────────────────
    const traceShortPath = (c) => {
        c.beginPath();
        c.moveTo(cvBucOff + cvSW, cvSW);
        c.lineTo(cvBucOff + cvBW - cvSW, cvSW);
        c.lineTo(cvLugOff + cvW - cvSW, cvS - cvSW);
        c.lineTo(cvLugOff + cvSW, cvS - cvSW);
        c.closePath();
    };

    const traceShortClipPath = (c) => {
        c.beginPath();
        c.moveTo(cvBucOff, 0);
        c.lineTo(cvBucOff + cvBW, 0);
        c.lineTo(cvLugOff + cvW, cvS);
        c.lineTo(cvLugOff, cvS);
        c.closePath();
    };

    // Layer -1: Outer drop shadow (subtle, matches live preview)
    ctx.save();
    ctx.translate(OX + cvShortL, OY + cvShortT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvS / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvS / 2); }
    ctx.shadowColor = 'rgba(26,17,11,0.18)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    traceShortPath(ctx);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.restore();

    // Layer 0: Texture layer
    ctx.save();
    ctx.translate(OX + cvShortL, OY + cvShortT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvS / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvS / 2); }
    traceShortClipPath(ctx);
    ctx.clip();
    ctx.fillStyle = '#E9E4DB';
    ctx.fill();
    if (img) { ctx.drawImage(img, -cvSPX, -cvSPY * cvCapYScale, cvTrueW, cvCapH); }
    ctx.restore();

    // Layer 1: Outer dark border
    ctx.save();
    ctx.translate(OX + cvShortL, OY + cvShortT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvS / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvS / 2); }
    traceShortPath(ctx);
    ctx.strokeStyle = '#0A0604';
    ctx.lineWidth = cvSW * 2 * 0.8;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();

    // Layer 3: Center highlight
    ctx.save();
    ctx.translate(OX + cvShortL, OY + cvShortT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvS / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvS / 2); }
    traceShortPath(ctx);
    ctx.clip();
    const shortHl = ctx.createLinearGradient(0, 0, cvFullW, 0);
    shortHl.addColorStop(0,    'rgba(255,252,248,0)');
    shortHl.addColorStop(0.36, 'rgba(255,252,248,0.03)');
    shortHl.addColorStop(0.5,  'rgba(255,252,248,0.13)');
    shortHl.addColorStop(0.64, 'rgba(255,252,248,0.03)');
    shortHl.addColorStop(1,    'rgba(255,252,248,0)');
    ctx.fillStyle = shortHl;
    ctx.fillRect(0, 0, cvFullW, cvS);
    ctx.restore();

    // Layer 4: Edge bevel highlight
    ctx.save();
    ctx.translate(OX + cvShortL, OY + cvShortT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvS / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvS / 2); }
    traceShortPath(ctx);
    ctx.strokeStyle = 'rgba(255,235,170,0.12)';
    ctx.lineWidth = cvSW * 2 * 0.3;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();

    // ── Long strap ───────────────────────────────────────────────────────────
    const traceLongPath = (c) => {
        c.beginPath();
        c.moveTo(cvLugOff + cvSW, cvSW);
        c.lineTo(cvLugOff + cvW - cvSW, cvSW);
        c.lineTo(cvBucOff + cvBW - cvSW, cvL - cvTip);
        c.quadraticCurveTo(cvBucOff + cvBW - cvSW, cvL - cvTip * 0.3, cvFullW / 2, cvL - cvSW);
        c.quadraticCurveTo(cvBucOff + cvSW, cvL - cvTip * 0.3, cvBucOff + cvSW, cvL - cvTip);
        c.closePath();
    };

    const traceLongClipPath = (c) => {
        c.beginPath();
        c.moveTo(cvLugOff, 0);
        c.lineTo(cvLugOff + cvW, 0);
        c.lineTo(cvBucOff + cvBW, cvL - cvTip);
        c.quadraticCurveTo(cvBucOff + cvBW, cvL - cvTip * 0.3, cvFullW / 2, cvL - 1.5 * screenRatio);
        c.quadraticCurveTo(cvBucOff, cvL - cvTip * 0.3, cvBucOff, cvL - cvTip);
        c.closePath();
    };

    // Layer -1: Outer drop shadow (subtle, matches live preview)
    ctx.save();
    ctx.translate(OX + cvLongL, OY + cvLongT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvL / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvL / 2); }
    ctx.shadowColor = 'rgba(26,17,11,0.18)';
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    traceLongPath(ctx);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.restore();

    // Layer 0: Texture layer
    ctx.save();
    ctx.translate(OX + cvLongL, OY + cvLongT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvL / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvL / 2); }
    traceLongClipPath(ctx);
    ctx.clip();
    ctx.fillStyle = '#E9E4DB';
    ctx.fill();
    if (img) { ctx.drawImage(img, -cvLPX, -cvLPY * cvCapYScale, cvTrueW, cvCapH); }
    ctx.restore();

    // Layer 1: Outer dark border
    ctx.save();
    ctx.translate(OX + cvLongL, OY + cvLongT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvL / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvL / 2); }
    traceLongPath(ctx);
    ctx.strokeStyle = '#0A0604';
    ctx.lineWidth = cvSW * 2 * 0.8;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();

    // Layer 3: Center highlight
    ctx.save();
    ctx.translate(OX + cvLongL, OY + cvLongT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvL / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvL / 2); }
    traceLongPath(ctx);
    ctx.clip();
    const longHl = ctx.createLinearGradient(0, 0, cvFullW, 0);
    longHl.addColorStop(0,    'rgba(255,252,248,0)');
    longHl.addColorStop(0.36, 'rgba(255,252,248,0.03)');
    longHl.addColorStop(0.5,  'rgba(255,252,248,0.13)');
    longHl.addColorStop(0.64, 'rgba(255,252,248,0.03)');
    longHl.addColorStop(1,    'rgba(255,252,248,0)');
    ctx.fillStyle = longHl;
    ctx.fillRect(0, 0, cvFullW, cvL);
    ctx.restore();

    // Layer 4: Edge bevel highlight
    ctx.save();
    ctx.translate(OX + cvLongL, OY + cvLongT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvL / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvL / 2); }
    traceLongPath(ctx);
    ctx.strokeStyle = 'rgba(255,235,170,0.12)';
    ctx.lineWidth = cvSW * 2 * 0.3;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();

    // Layer 8: Holes with depth (inside clip)
    ctx.save();
    ctx.translate(OX + cvLongL, OY + cvLongT);
    if (strapRotation !== 0) { ctx.translate(cvFullW / 2, cvL / 2); ctx.rotate(strapRotation * Math.PI / 180); ctx.translate(-cvFullW / 2, -cvL / 2); }
    traceLongPath(ctx);
    ctx.clip();
    for (let i = 0; i < 7; i++) {
        const hy = cvL - cvTip - (4 * ms) - i * (6.5 * ms);
        const hr = 0.85 * ms;
        // outer shadow halo
        ctx.beginPath();
        ctx.arc(cvFullW / 2, hy, hr * 1.7, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.22)';
        ctx.fill();
        // deep hole (radial gradient)
        const holeGrad = ctx.createRadialGradient(cvFullW / 2 - hr * 0.3, hy - hr * 0.3, 0, cvFullW / 2, hy, hr);
        holeGrad.addColorStop(0,   '#060300');
        holeGrad.addColorStop(0.55,'#1A110B');
        holeGrad.addColorStop(1,   'rgba(40,25,15,0.55)');
        ctx.beginPath();
        ctx.arc(cvFullW / 2, hy, hr, 0, Math.PI * 2);
        ctx.fillStyle = holeGrad;
        ctx.fill();
        // specular glint
        ctx.beginPath();
        ctx.arc(cvFullW / 2 - hr * 0.25, hy - hr * 0.28, hr * 0.28, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.07)';
        ctx.fill();
    }
    ctx.restore();

    const base64Image = canvas.toDataURL('image/png');

    // ── Leather hide overview with cut area punched out ───────────────────────
    let hideOverview = '';
    if (img && img.naturalWidth > 0) {
        const MAX_W  = 800;
        const ovScale = Math.min(MAX_W / trueWidth, 1);
        const ovW = Math.round(trueWidth * ovScale);
        const ovH = Math.round(capDisplayHeight * ovScale);

        const hc = document.createElement('canvas');
        hc.width  = ovW;
        hc.height = ovH;
        const hctx = hc.getContext('2d');

        hctx.drawImage(img, 0, 0, ovW, ovH);
        // Dark overlay on cut area — không dùng destination-out để có thể export JPEG
        hctx.fillStyle = 'rgba(0, 0, 0, 0.82)';

        if (cutOption === 'preselected' && preSelectedSize) {
            hctx.fillRect(
                preSelectedSize.xRatio * ovW,
                preSelectedSize.yRatio * ovH,
                preSelectedSize.wRatio * ovW,
                preSelectedSize.hRatio * ovH
            );
            if (preSelectedSize.shortXRatio !== undefined) {
                hctx.fillRect(
                    preSelectedSize.shortXRatio * ovW,
                    preSelectedSize.shortYRatio * ovH,
                    preSelectedSize.shortWRatio * ovW,
                    preSelectedSize.shortHRatio * ovH
                );
            }
        } else {
            const oCW     = vWidth  * ovScale;
            const oCBW    = vBuckleWidth * ovScale;
            const oCS     = vShort  * ovScale;
            const oCL     = vLong   * ovScale;
            const oFullW  = Math.max(oCW, oCBW);
            const oLugOff = (oFullW - oCW) / 2;
            const oBucOff = (oFullW - oCBW) / 2;
            const oLT     = tipH    * ovScale;

            const oSX = shortPos.x * ovScale;
            const oSY = shortPos.y * capYScale * ovScale;
            const oLX = longPos.x  * ovScale;
            const oLY = longPos.y  * capYScale * ovScale;

            hctx.beginPath();
            hctx.moveTo(oSX + oBucOff, oSY);
            hctx.lineTo(oSX + oBucOff + oCBW, oSY);
            hctx.lineTo(oSX + oLugOff + oCW, oSY + oCS);
            hctx.lineTo(oSX + oLugOff, oSY + oCS);
            hctx.closePath();
            hctx.fill();

            hctx.fill(new Path2D(
                `M ${oLX + oLugOff} ${oLY} ` +
                `L ${oLX + oLugOff + oCW} ${oLY} ` +
                `L ${oLX + oBucOff + oCBW} ${oLY + oCL - oLT} ` +
                `Q ${oLX + oBucOff + oCBW} ${oLY + oCL - oLT * 0.3} ${oLX + oFullW / 2} ${oLY + oCL} ` +
                `Q ${oLX + oBucOff} ${oLY + oCL - oLT * 0.3} ${oLX + oBucOff} ${oLY + oCL - oLT} Z`
            ));
        }

        hideOverview = hc.toDataURL('image/jpeg', 0.82);
    }

    return { base64Image, hideOverview };
}
